// All-posts list, grouped by year. Each row is a date column + main column +
// a circular arrow that fills indigo on hover.
import { Link } from "react-router";

import { AuthorAvatar } from "./AuthorAvatar";
import type { BlogPost } from "./posts";

function normalizeInternalPath(href: string) {
  return href.replace(/\/$/, "") || "/";
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function monthShort(iso: string) {
  return MONTHS[Number(iso.slice(5, 7)) - 1];
}

function dayOf(iso: string) {
  return String(Number(iso.slice(8, 10)));
}

type PostListProps = {
  posts: BlogPost[];
};

export function PostList({ posts }: PostListProps) {
  // Group by year, newest year first.
  const byYear: Record<string, BlogPost[]> = {};
  for (const p of posts) {
    const year = p.date.slice(0, 4);
    (byYear[year] ||= []).push(p);
  }
  const groups = Object.entries(byYear).sort(
    ([a], [b]) => Number(b) - Number(a),
  );

  return (
    <section className="bg-white px-[clamp(24px,5vw,80px)] pb-[clamp(72px,9vw,112px)]">
      <div className="mx-auto max-w-[1180px]">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-6 border-b border-grindigo-100 pb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-indigo-300" />
              <div className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-indigo-700">
                All posts
              </div>
            </div>
            <h2 className="font-companion mt-3 text-[clamp(28px,3.2vw,36px)] font-bold leading-[1.1] tracking-[-0.022em] text-primary">
              The full archive.
            </h2>
          </div>
        </div>

        {posts.length === 0 ? (
          <div className="rounded-[18px] border border-dashed border-grindigo-200 px-6 py-16 text-center text-secondary">
            No posts yet — check back soon.
          </div>
        ) : (
          groups.map(([year, yearPosts]) => (
            <div key={year} className="mb-14">
              {groups.length > 1 && (
                <div className="mb-5 flex items-center gap-3.5">
                  <div className="font-companion text-[22px] font-bold tracking-[-0.02em] text-primary">
                    {year}
                  </div>
                  <div className="h-px flex-1 bg-grindigo-100" />
                  <div className="text-xs font-medium text-secondary">
                    {yearPosts.length}{" "}
                    {yearPosts.length === 1 ? "post" : "posts"}
                  </div>
                </div>
              )}

              <ul className="m-0 list-none p-0">
                {yearPosts.map((p, i) => (
                  <PostRow
                    key={p.id}
                    post={p}
                    last={i === yearPosts.length - 1}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

type PostRowProps = {
  post: BlogPost;
  last: boolean;
};

function PostRow({ post, last }: PostRowProps) {
  const isComingSoon = !post.href;
  const rowClassName = `post-row group grid grid-cols-1 items-center gap-3 py-6 no-underline transition-transform duration-[250ms] ease-[ease] sm:grid-cols-[120px_1fr_auto] sm:gap-7 ${
    last ? "" : "border-b border-grindigo-100"
  }`;

  const body = <PostRowBody post={post} isComingSoon={isComingSoon} />;

  return (
    <li>
      {post.href ? (
        <Link to={normalizeInternalPath(post.href)} className={rowClassName}>
          {body}
        </Link>
      ) : (
        <div className={rowClassName}>{body}</div>
      )}
    </li>
  );
}

type PostRowBodyProps = { post: BlogPost; isComingSoon: boolean };

function PostRowBody({ post, isComingSoon }: PostRowBodyProps) {
  return (
    <>
      {/* Date column */}
      <div className="font-companion leading-[1.1]">
        <div className="text-[13px] font-semibold uppercase tracking-[0.04em] text-secondary">
          {monthShort(post.date)}
        </div>
        <div className="mt-0.5 text-[36px] font-bold tracking-[-0.025em] text-primary">
          {dayOf(post.date)}
        </div>
      </div>

      {/* Main column */}
      <div className="min-w-0">
        {isComingSoon && (
          <div className="mb-2 flex flex-wrap items-center gap-2.5">
            <span className="rounded-full bg-grindigo-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-grindigo-500">
              Coming soon
            </span>
          </div>
        )}
        <h3 className="font-companion m-0 mb-2 text-[clamp(20px,2vw,24px)] font-semibold leading-[1.2] tracking-[-0.018em] text-primary transition-colors duration-150 ease-[ease] group-hover:text-indigo-600 [text-wrap:balance]">
          {post.title}
        </h3>
        <p className="m-0 mb-3 max-w-[720px] text-[15px] leading-[1.55] text-secondary [text-wrap:pretty]">
          {post.dek}
        </p>
        <div className="flex items-center gap-2.5">
          <AuthorAvatar author={post.author} mascot={post.mascot} size={22} />
          <span className="text-[13px] text-secondary">
            <span className="font-semibold text-primary">
              {post.author.name}
            </span>
            {post.author.role ? ` · ${post.author.role}` : ""}
          </span>
        </div>
      </div>

      {/* Arrow */}
      <div className="hidden h-12 w-12 shrink-0 place-items-center rounded-full bg-indigo-50 text-indigo-700 transition-[transform,background,color] duration-150 ease-[ease] group-hover:translate-x-1 group-hover:bg-indigo-600 group-hover:text-white sm:grid">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12h14M13 5l7 7-7 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </>
  );
}
